import React, { useEffect, useState, useMemo } from 'react';
import { render, Box, Text, useApp, useInput } from 'ink';
import { existsSync, readFileSync, statSync, openSync, readSync, closeSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import type { ManagerStatus } from '../lib/watchdog.js';
import { getManagerPaths } from '../lib/watchdog.js';

// ENG-4555: TUI dashboard for the manager. Reads ~/.augmented/manager-state.json
// (already maintained by manager-worker.ts:880) and tails ~/.augmented/manager.log
// so users can observe a running manager without watching log lines scroll past.
//
// Read-only in v1 — no agent-level actions (restart/pause/etc). Falls back to a
// log-streaming message if stdout is not a TTY (CI, scripts) since Ink's full-screen
// rendering can't function without raw input mode.

interface ManagerWatchOptions {
  configDir?: string;
  noTui?: boolean;
}

const REFRESH_MS = 1000;
const LOG_TAIL_LINES = 200;
const DETAIL_RECENT_LINES = 50;

export function managerWatchCommand(opts: ManagerWatchOptions = {}): void {
  const configDir = opts.configDir ?? join(homedir(), '.augmented');
  const paths = getManagerPaths(configDir);

  // Non-TTY environments (CI, piped output) can't run a TUI — Ink relies on
  // raw stdin and ANSI cursor control. Fall back to log streaming so the
  // command still produces useful output when scripted.
  const isTty = process.stdout.isTTY === true && process.stdin.isTTY === true;
  if (opts.noTui || !isTty) {
    streamLogFile(paths.logFile);
    return;
  }

  // Switch to the terminal's alternate screen buffer (like vim/htop/less) so
  // the TUI takes over the whole window and the user's scrollback is restored
  // verbatim on exit. \x1b[?1049h enters the alt buffer + clears it,
  // \x1b[?1049l restores the prior buffer. Hide the cursor while we render
  // (\x1b[?25l) and re-show on exit (\x1b[?25h) so it doesn't blink under
  // selected boxes.
  process.stdout.write('\x1b[?1049h\x1b[H\x1b[?25l');
  const restore = (): void => { process.stdout.write('\x1b[?25h\x1b[?1049l'); };

  // Restore on every exit path: clean exit (waitUntilExit resolves), thrown
  // exception, SIGINT/SIGTERM mid-render, or hard-quit on Ctrl-C. Without
  // this the user's terminal stays on the alt buffer with the cursor hidden.
  const onSignal = (): void => { restore(); process.exit(0); };
  process.once('SIGINT', onSignal);
  process.once('SIGTERM', onSignal);
  process.once('exit', restore);

  const { waitUntilExit } = render(<Dashboard stateFile={paths.stateFile} logFile={paths.logFile} />);
  waitUntilExit().catch(() => { /* ignore */ }).finally(restore);
}

// ---------------------------------------------------------------------------
// State + log readers
// ---------------------------------------------------------------------------

function readState(stateFile: string): ManagerStatus | null {
  try {
    if (!existsSync(stateFile)) return null;
    const raw = readFileSync(stateFile, 'utf-8');
    return JSON.parse(raw) as ManagerStatus;
  } catch {
    return null;
  }
}

// Tail the last N lines of a log file. Reads only the trailing window so this
// stays cheap even when the log grows to many MB. Returns lines oldest-first.
function tailLogFile(logFile: string, lines: number): string[] {
  if (!existsSync(logFile)) return [];
  try {
    const fileSize = statSync(logFile).size;
    if (fileSize === 0) return [];
    // Heuristic: ~200 chars/line average — we read 4× headroom and slice. For
    // the manager log this comfortably fits the requested tail in one read.
    const readSize = Math.min(fileSize, lines * 800);
    const fd = openSync(logFile, 'r');
    try {
      const buf = Buffer.alloc(readSize);
      readSync(fd, buf, 0, readSize, fileSize - readSize);
      const text = buf.toString('utf-8');
      const all = text.split('\n').filter((l) => l.length > 0);
      return all.slice(-lines);
    } finally {
      closeSync(fd);
    }
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Ink components
// ---------------------------------------------------------------------------

interface DashboardProps {
  stateFile: string;
  logFile: string;
}

const Dashboard: React.FC<DashboardProps> = ({ stateFile, logFile }) => {
  const { exit } = useApp();
  const [status, setStatus] = useState<ManagerStatus | null>(() => readState(stateFile));
  const [logLines, setLogLines] = useState<string[]>(() => tailLogFile(logFile, LOG_TAIL_LINES));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [view, setView] = useState<'grid' | 'detail'>('grid');
  const [tick, setTick] = useState(0);

  // Refresh state + log tail on a fixed cadence. We don't use fs.watch because
  // the manager rewrites the state file atomically and watch events on macOS
  // are notoriously chatty/unreliable for that pattern.
  useEffect(() => {
    const id = setInterval(() => {
      setStatus(readState(stateFile));
      setLogLines(tailLogFile(logFile, LOG_TAIL_LINES));
      setTick((t) => t + 1);
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [stateFile, logFile]);

  const agents = status?.agents ?? [];
  const selected = agents[selectedIndex];

  useInput((input, key) => {
    // Detail view owns 'q' first — pressing 'q' there means "back to grid",
    // not "quit the app". Only the grid view treats bare 'q' as exit. Ctrl-C
    // remains a hard-quit in either view because users universally expect it.
    if (view === 'detail') {
      if (key.escape || input === 'q') setView('grid');
      else if (key.ctrl && input === 'c') exit();
      return;
    }
    if (input === 'q' || (key.ctrl && input === 'c')) {
      exit();
      return;
    }
    if (agents.length === 0) return;
    if (key.upArrow || key.leftArrow) {
      setSelectedIndex((i) => (i - 1 + agents.length) % agents.length);
    } else if (key.downArrow || key.rightArrow) {
      setSelectedIndex((i) => (i + 1) % agents.length);
    } else if (key.return) {
      setView('detail');
    }
  });

  if (!status) {
    return (
      <Box flexDirection="column" padding={1}>
        <Text bold color="yellow">⚠ Manager state not found</Text>
        <Text> </Text>
        <Text>Looked for: <Text color="gray">{stateFile}</Text></Text>
        <Text> </Text>
        <Text>Start the manager first: <Text color="cyan">agt manager start</Text></Text>
        <Text dimColor>Press <Text color="white">q</Text> to quit.</Text>
      </Box>
    );
  }

  if (view === 'detail' && selected) {
    return <DetailView agent={selected} logLines={logLines} />;
  }

  return <GridView status={status} agents={agents} selectedIndex={selectedIndex} logLines={logLines} tick={tick} />;
};

// ---------------------------------------------------------------------------
// Grid view — one box per agent
// ---------------------------------------------------------------------------

interface GridViewProps {
  status: ManagerStatus;
  agents: ManagerStatus['agents'];
  selectedIndex: number;
  logLines: string[];
  tick: number;
}

const GridView: React.FC<GridViewProps> = ({ status, agents, selectedIndex, logLines, tick }) => {
  const lastLogLine = logLines[logLines.length - 1] ?? '';
  return (
    <Box flexDirection="column">
      <HeaderBar status={status} tick={tick} />
      {agents.length === 0 ? (
        <Box padding={1}>
          <Text dimColor>No agents discovered yet. Manager is polling…</Text>
        </Box>
      ) : (
        <Box flexDirection="row" flexWrap="wrap" paddingX={1}>
          {agents.map((agent, i) => (
            <AgentBox
              key={agent.agentId}
              agent={agent}
              selected={i === selectedIndex}
              recentLogLine={mostRecentLineForAgent(logLines, agent.codeName)}
            />
          ))}
        </Box>
      )}
      <Footer tail={lastLogLine} />
    </Box>
  );
};

const HeaderBar: React.FC<{ status: ManagerStatus; tick: number }> = ({ status, tick }) => {
  // The tick counter forces a redraw every refresh so the "last poll" relative
  // time stays current even when nothing else changed. Reading `tick` here is
  // intentional — without it React would skip the re-render.
  void tick;
  return (
    <Box paddingX={1} paddingY={0} borderStyle="single" borderColor="cyan">
      <Box flexDirection="row" justifyContent="space-between" width="100%">
        <Text bold color="cyan">agt manager · {status.agents.length} agent{status.agents.length === 1 ? '' : 's'}</Text>
        <Text dimColor>
          PID {status.pid} · polls {status.pollCount} · errors {status.errorCount} · last poll {relativeTime(status.lastPollAt)}
        </Text>
      </Box>
    </Box>
  );
};

const Footer: React.FC<{ tail: string }> = ({ tail }) => (
  <Box paddingX={1} flexDirection="column">
    <Box>
      <Text dimColor>↑↓ select · </Text>
      <Text dimColor>Enter detail · </Text>
      <Text dimColor>q quit</Text>
    </Box>
    {tail && (
      <Box>
        <Text dimColor>last log: </Text>
        <Text>{truncate(tail, process.stdout.columns ? process.stdout.columns - 12 : 100)}</Text>
      </Box>
    )}
  </Box>
);

// ---------------------------------------------------------------------------
// Agent box (grid cell)
// ---------------------------------------------------------------------------

interface AgentBoxProps {
  agent: ManagerStatus['agents'][number];
  selected: boolean;
  recentLogLine: string | null;
}

const AgentBox: React.FC<AgentBoxProps> = ({ agent, selected, recentLogLine }) => {
  const isError = agent.status !== 'active' && agent.status !== 'paused' && agent.status !== '';
  const borderColor = selected ? 'yellow' : isError ? 'red' : 'gray';
  const statusColor = agent.status === 'active' ? 'green' : agent.status === 'paused' ? 'yellow' : isError ? 'red' : 'gray';
  const gateway = agent.gatewayRunning
    ? `:${agent.gatewayPort} (PID ${agent.gatewayPid})`
    : agent.gatewayPort
      ? `:${agent.gatewayPort} down`
      : '—';
  return (
    <Box flexDirection="column" borderStyle="round" borderColor={borderColor} paddingX={1} marginRight={1} marginBottom={0} width={42}>
      <Box>
        <Text bold>{agent.codeName}</Text>
        <Text> </Text>
        <Text color={statusColor}>{agent.status || 'unknown'}</Text>
      </Box>
      <Text dimColor>charter {agent.charterVersion || '—'} · tools {agent.toolsVersion || '—'}</Text>
      <Text dimColor>gateway {gateway}</Text>
      <Text dimColor>acp sessions {agent.acpSessions?.length ?? 0}</Text>
      <Text dimColor>last provision {relativeTime(agent.lastProvisionAt)}</Text>
      {recentLogLine && (
        <Text>
          <Text color="cyan">▸</Text> {truncate(recentLogLine, 36)}
        </Text>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Detail view — recent activity for the selected agent
// ---------------------------------------------------------------------------

interface DetailViewProps {
  agent: ManagerStatus['agents'][number];
  logLines: string[];
}

const DetailView: React.FC<DetailViewProps> = ({ agent, logLines }) => {
  const recent = useMemo(
    () => logLines.filter((l) => l.toLowerCase().includes(agent.codeName.toLowerCase())).slice(-DETAIL_RECENT_LINES),
    [logLines, agent.codeName],
  );
  return (
    <Box flexDirection="column">
      <Box paddingX={1} borderStyle="double" borderColor="cyan">
        <Text bold color="cyan">{agent.codeName}</Text>
        <Text> · </Text>
        <Text>{agent.status || 'unknown'}</Text>
        <Text dimColor> · charter {agent.charterVersion || '—'} · tools {agent.toolsVersion || '—'}</Text>
      </Box>
      <Box paddingX={1} marginTop={1} flexDirection="column">
        <Text bold>Gateway</Text>
        <Text>
          {agent.gatewayRunning
            ? `running on :${agent.gatewayPort} (PID ${agent.gatewayPid})`
            : agent.gatewayPort
              ? `configured :${agent.gatewayPort} but not running`
              : 'no gateway configured'}
        </Text>
      </Box>
      <Box paddingX={1} marginTop={1} flexDirection="column">
        <Text bold>ACP sessions ({agent.acpSessions?.length ?? 0})</Text>
        {(agent.acpSessions ?? []).length === 0 ? (
          <Text dimColor>none</Text>
        ) : (
          (agent.acpSessions ?? []).map((s) => (
            <Text key={s.sessionId}>
              · {s.agentCommand} {s.sessionName ?? ''} · {s.queueState} · turns {s.turnCount} · started {relativeTime(s.startedAt)}
            </Text>
          ))
        )}
      </Box>
      <Box paddingX={1} marginTop={1} flexDirection="column">
        <Text bold>Recent activity ({recent.length} lines, filtered to "{agent.codeName}")</Text>
        {recent.length === 0 ? (
          <Text dimColor>nothing in the log mentions this agent yet</Text>
        ) : (
          recent.map((line, i) => (
            <Text key={i}>{truncate(line, process.stdout.columns ? process.stdout.columns - 4 : 120)}</Text>
          ))
        )}
      </Box>
      <Box paddingX={1} marginTop={1}>
        <Text dimColor>esc / q · back to grid</Text>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function mostRecentLineForAgent(lines: string[], codeName: string): string | null {
  for (let i = lines.length - 1; i >= 0; i--) {
    const line = lines[i];
    if (line && line.toLowerCase().includes(codeName.toLowerCase())) return line;
  }
  return null;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 'never';
  const diff = Math.max(0, Date.now() - t);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, Math.max(0, max - 1))}…` : s;
}

// ---------------------------------------------------------------------------
// Headless fallback — stream the log file
// ---------------------------------------------------------------------------

function streamLogFile(logFile: string): void {
  if (!existsSync(logFile)) {
    process.stderr.write(`No manager log found at ${logFile}.\nStart the manager first: agt manager start\n`);
    process.exitCode = 1;
    return;
  }
  // Print existing content, then poll for appends. Pure node — no external
  // tail-equivalent library so this works on every host the CLI ships to.
  let lastSize = 0;
  try {
    const initial = readFileSync(logFile, 'utf-8');
    process.stdout.write(initial);
    lastSize = statSync(logFile).size;
  } catch (err) {
    process.stderr.write(`Failed to read log: ${(err as Error).message}\n`);
    process.exitCode = 1;
    return;
  }
  const id = setInterval(() => {
    try {
      const size = statSync(logFile).size;
      // Truncation / rotation: if the file is smaller than we last saw, the
      // log has been rotated or truncated. Reset lastSize to 0 so we resume
      // emitting from the start of the new file on the next tick (or the
      // remainder of this tick, if size > 0). Without this, every subsequent
      // append would be silently swallowed.
      if (size < lastSize) lastSize = 0;
      if (size === lastSize) return;
      const fd = openSync(logFile, 'r');
      try {
        const buf = Buffer.alloc(size - lastSize);
        readSync(fd, buf, 0, buf.length, lastSize);
        process.stdout.write(buf.toString('utf-8'));
        lastSize = size;
      } finally {
        closeSync(fd);
      }
    } catch { /* file deleted between stat and read — keep polling */ }
  }, 500);
  const stop = (): void => {
    clearInterval(id);
    process.exit(0);
  };
  process.on('SIGINT', stop);
  process.on('SIGTERM', stop);
}
